import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import CircularSpinner from 'shared/atoms/CircularSpinner';
import Button from 'shared/atoms/Button';
import ProgressBar from 'shared/atoms/ProgressBar';
import Tooltip from 'shared/atoms/Tooltip';

import Table, { CELL_TYPES, ORDER_TYPES } from 'shared/organisms/Table';

import theme from 'shared/themes/default';

import PlaylistActivityList from './PlaylistActivityList';

import { useWatchData } from '../../../Context';
import { listPlaylistWatchData } from '../../../Context/graphql/queries';

const SpinnerWrapper = styled.div`
  padding-top: ${theme.spacing.m2};
`;

export const StyledProgressWrapper = styled.div`
  cursor: pointer;
`;

const PlaylistGroupsWatchDataTableRowItem = ({
  item,
  playlistId,
  handleReminder,
  open,
  organizationId,
  showReminder,
}) => {
  const { t } = useTranslation();
  const { useLazyGraphQLQuery } = useWatchData();

  const [listPlaylistWatchDataQuery, listPlaylistWatchDataResponse] =
    useLazyGraphQLQuery(listPlaylistWatchData);

  const watchHistory =
    listPlaylistWatchDataResponse.data?.listPlaylistWatchData || [];

  useEffect(() => {
    if (open) {
      listPlaylistWatchDataQuery({
        variables: {
          groupId: item.groupId,
          playlistId,
          organizationId,
        },
      });
    }
  }, [open]);

  const columns = [
    {
      key: 'name',
      label: t('watchData.name'),
      expandedSticky: true,
      fixedWidth: '23%',
      maskValue: true,
    },
    {
      key: 'email',
      label: t('watchData.email'),
      fixedWidth: '28%',
      maskValue: true,
    },
    {
      key: 'progress',
      label: t('watchData.progress'),
      type: CELL_TYPES.COMPONENT,
      render: (row) => (
        <Tooltip
          title={
            <PlaylistActivityList
              organizationId={organizationId}
              playlistId={playlistId}
              userId={row.userId}
            />
          }
          placement="top"
          arrow
        >
          <StyledProgressWrapper>
            <ProgressBar progress={row.progress} />
          </StyledProgressWrapper>
        </Tooltip>
      ),
    },
    {
      key: 'lastReminderAt',
      label: t('watchData.lastReminder'),
      type: CELL_TYPES.DATE_TIME,
    },
  ];

  if (showReminder) {
    columns.push({
      key: 'sendReminder',
      label: t('watchData.sendNewReminder'),
      disableSorting: true,
      type: CELL_TYPES.COMPONENT,
      render: (row) => (
        <Button
          ariaLabel={t('watchData.sendReminder')}
          type="text"
          onClick={() =>
            handleReminder(row.userId, playlistId, row.lastReminderAt)
          }
          small
        >
          {t('watchData.sendReminder')}
        </Button>
      ),
    });
  }

  return listPlaylistWatchDataResponse.loading ? (
    <SpinnerWrapper>
      <CircularSpinner static />
    </SpinnerWrapper>
  ) : (
    watchHistory.length > 0 && (
      <Table
        ariaLabel="Expandable watch data row inner content"
        columns={columns}
        items={watchHistory}
        defaultOrder={ORDER_TYPES.DESC}
        defaultOrderBy="progress"
        isExpandedType
        isExpandedOpen={open}
      />
    )
  );
};

PlaylistGroupsWatchDataTableRowItem.propTypes = {
  item: PropTypes.object.isRequired,
  handleReminder: PropTypes.func.isRequired,
  open: PropTypes.bool,
  organizationId: PropTypes.string.isRequired,
  playlistId: PropTypes.string.isRequired,
  showReminder: PropTypes.bool,
};

PlaylistGroupsWatchDataTableRowItem.defaultProps = {
  open: false,
  showReminder: false,
};

export default PlaylistGroupsWatchDataTableRowItem;
